import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./ProjectDetail.module.css";
import { projects } from "../data/projects";
import { useDockAnimation } from "../hooks/useDockAnimation";
import { useSound } from "../context/SoundContext";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

const sliderVariants = {
  incoming: (direction) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  active: { x: 0, opacity: 1 },
  outgoing: (direction) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

// Full Screen Modal Component
const FullScreenModal = ({ images, activeIndex, setActiveIndex, onClose }) => {
  const { playClick } = useSound();
  
  const handleNext = (e) => {
    e.stopPropagation();
    playClick();
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    playClick();
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setActiveIndex((prev) => (prev + 1) % images.length);
      if (e.key === "ArrowLeft") setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, onClose, setActiveIndex]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>
        <button className={`${styles.modalNavButton} ${styles.modalPrev}`} onClick={handlePrev}>
          <span className={styles.rotate180}>
             <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px" fill="#ffffff">
                <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
              </svg>
          </span>
        </button>
        
        <img src={images[activeIndex]} alt="Full screen project" className={styles.modalImage} />

        <button className={`${styles.modalNavButton} ${styles.modalNext}`} onClick={handleNext}>
           <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px" fill="#ffffff">
              <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
            </svg>
        </button>
      </div>
    </div>
  );
};

const MainImageViewer = ({
  images,
  activeIndex,
  setActiveIndex,
  imageDimensions,
  setImageDimensions,
  openModal // Receive function to open modal
}) => {
  const [[imageIndex, direction], setImageIndex] = useState([activeIndex, 0]);
  const prevActiveIndex = useRef(activeIndex);
  const [isHovered, setIsHovered] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const { playClick } = useSound();
  const isDragging = useRef(false); // Track if a drag is occurring

  React.useEffect(() => {
    if (activeIndex !== prevActiveIndex.current) {
      let newDirection = activeIndex > prevActiveIndex.current ? 1 : -1;
      // Handle loop from last to first
      if (
        prevActiveIndex.current === images.length - 1 &&
        activeIndex === 0
      ) {
        newDirection = 1;
      }
      // Handle loop from first to last
      if (
        prevActiveIndex.current === 0 &&
        activeIndex === images.length - 1
      ) {
        newDirection = -1;
      }
      setImageIndex([activeIndex, newDirection]);
      prevActiveIndex.current = activeIndex;
    }
  }, [activeIndex, images.length]);

  const handleImageLoad = () => {
    if (imageRef.current && containerRef.current) {
      const {
        offsetWidth: containerWidth,
        offsetHeight: containerHeight,
      } = containerRef.current;
      const { offsetWidth: imageWidth, offsetHeight: imageHeight } =
        imageRef.current;

      setImageDimensions({
        width: imageWidth,
        height: imageHeight,
        top: (containerHeight - imageHeight) / 2,
        left: (containerWidth - imageWidth) / 2,
      });
    }
  };

  const navigateImage = (newDirection) => {
    playClick();
    const newIndex =
      (imageIndex + newDirection + images.length) % images.length;
    setActiveIndex(newIndex);
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  return (
    <div
      ref={containerRef}
      className={styles.mainImageViewer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.img
          ref={imageRef}
          onLoad={handleImageLoad}
          key={imageIndex}
          src={images[imageIndex]}
          alt={`Project image ${imageIndex + 1}`}
          className={styles.mainImage}
          variants={sliderVariants}
          initial="incoming"
          animate="active"
          exit="outgoing"
          custom={direction}
          transition={{ duration: 0.5 }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragStart={() => { isDragging.current = true; }}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            
            // Short timeout to reset dragging state so click handler doesn't fire immediately after drag
            setTimeout(() => { isDragging.current = false; }, 50);

            if (swipe < -swipeConfidenceThreshold) {
              navigateImage(1);
            } else if (swipe > swipeConfidenceThreshold) {
              navigateImage(-1);
            }
          }}
          onClick={() => {
             // Only open modal if we were NOT dragging
             if (!isDragging.current) {
               openModal();
             }
          }}
          style={{ cursor: 'zoom-in' }} // Add zoom-in cursor hint
        />
      </AnimatePresence>
      {isHovered && imageDimensions && (
        <div
          className={styles.buttonContainer}
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
            top: imageDimensions.top,
            left: imageDimensions.left,
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
            className={`${styles.navButton} ${styles.prevButton}`}
          >
            <span className={styles.rotate180}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#ffffff"
              >
                <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
              </svg>
            </span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
            className={`${styles.navButton} ${styles.nextButton}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#ffffff"
            >
              <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

const ProjectDetailImages = ({ images, setActiveIndex }) => {
  const {
    currentScales,
    currentPositions,
    handleMouseMove,
    handleMouseLeave,
    baseIconSize,
  } = useDockAnimation(images);
  const { playHover, playClick } = useSound();

  return (
    <div
      className={styles.projectImageGrid}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {images.map((image, index) => {
        const scale = currentScales[index];
        const position = currentPositions[index] || 0;
        const scaledSize = baseIconSize * scale;

        return (
          <motion.div
            key={index}
            className={styles.projectGalleryImageContainer}
            onMouseEnter={playHover}
            onClick={() => {
              playClick();
              setActiveIndex(index);
            }}
            style={{
              position: "absolute",
              left: `${position - scaledSize / 2}px`,
              bottom: "0px",
              width: `${scaledSize}px`,
              height: `${scaledSize}px`,
              transformOrigin: "bottom center",
              zIndex: Math.round(scale * 10),
            }}
          >
            <img
              src={image}
              alt={`Project thumbnail ${index + 1}`}
              className={styles.projectGalleryImage}
            />
          </motion.div>
        );
      })}
    </div>
  );
};

// Description component for the LEFT sidebar
const Description = ({ projectId }) => {
  const project = projects.find((p) => p.id === projectId);

  if (!project) {
    return <div>Project not found.</div>;
  }

  return (
    <div>
      <h2 className={styles.projectTitle}>{project.title}</h2>
      {project.description &&
        project.description.map((p, i) => (
          <p key={i} className={styles.description}>
            {p}
          </p>
        ))}
      {project.tags && (
        <div className={styles.tagContainer}>
          {project.tags.map((tag) => (
            <button key={tag} className={styles.tag} onClick={() => console.log('Project Tag clicked:', tag)}>
              {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Main content for the RIGHT column
const Content = ({ projectId, activeIndex: initialActiveIndex }) => {
  const project = projects.find((p) => p.id === projectId);
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex || 0);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!project) {
    return <div>Project content not available.</div>;
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="in"
      exit="out"
      transition={{ duration: 0.5 }}
    >
      <div className={styles.header}>
        <span className={styles.projectDate}>{project.date}</span>
      </div>
      {project.images && project.images.length > 0 ? (
        <>
          <MainImageViewer
            images={project.images}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            imageDimensions={imageDimensions}
            setImageDimensions={setImageDimensions}
            openModal={() => setIsModalOpen(true)}
          />
          <div className={styles.thumbnailWrapper}>
            <div style={{ width: imageDimensions ? imageDimensions.width : 0 }}>
              <ProjectDetailImages
                images={project.images}
                setActiveIndex={setActiveIndex}
              />
            </div>
          </div>
          
          {isModalOpen && (
            <FullScreenModal 
              images={project.images}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </>
      ) : (
        project.heroImage && (
          <img
            src={project.heroImage}
            alt={project.title}
            className={styles.heroImage}
          />
        )
      )}
    </motion.div>
  );
};

// Export both components
const ProjectDetail = { Description, Content };
export default ProjectDetail;